import { useMemo } from "react";
import { t } from "@/i18n-js/instance";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import { useIsPropertiesPanelRevampEnabled } from "@circle-react-shared/BuilderUI/Hooks/useIsPropertiesPanelRevamp";
import type { PropsMeta } from "@circle-react-shared/BuilderUI/PropsMeta.types";
import type { StyleMetaAttributes } from "@circle-react-shared/BuilderUI/PropsMetaStyle.types";
import { AlignmentField } from "./fields/AlignmentField";
import { BooleanField } from "./fields/BooleanField";
import { BorderRadiusSelector } from "./fields/BorderRadius";
import { BoxModelInput } from "./fields/BoxModelField";
import { ColorField } from "./fields/ColorField";
import { CustomHtmlField } from "./fields/CustomHtmlField";
import { DimensionInput } from "./fields/DimensionInput";
import type { Unit } from "./fields/DimensionInput/constants";
import { EmbedField } from "./fields/EmbedField";
import { FontPicker } from "./fields/FontPicker";
import { FontWeight } from "./fields/FontWeight";
import { FormSelectField } from "./fields/FormSelectField";
import { HeadingLevelPicker } from "./fields/HeadingLevelPicker";
import { ImagePickerField } from "./fields/ImagePickerField";
import { ListField } from "./fields/ListField";
import { NumberField } from "./fields/NumberField";
import { ParagraphLevelPicker } from "./fields/ParagraphLevelPicker";
import { RichTextField } from "./fields/RichTextField";
import { SelectField } from "./fields/SelectField";
import { Sizing } from "./fields/Sizing";
import { SliderField } from "./fields/SliderField";
import { StringField } from "./fields/StringField";
import { TextareaField } from "./fields/TextareaField";
import { UrlField } from "./fields/UrlField";
import { VideoUploader } from "./fields/VideoUploader";
import { VisibilityField } from "./fields/VisibilityField";
import { WrapField } from "./fields/WrapField";
// import { DisplayField } from "./fields/DisplayField";
// import { DirectionField } from "./fields/DirectionField";

export interface EditorFieldProps {
  name: string;
  propMeta: PropsMeta | StyleMetaAttributes;
}

export const DefaultEditorFields = ({ name, propMeta }: EditorFieldProps) => {
  const customEditorFields = useBuilderStore(state => state.customEditorFields);
  const isPropertiesPanelV2 = useIsPropertiesPanelRevampEnabled();

  const label = useMemo(() => {
    const fallbackLabel = propMeta.name;
    const i18nKey = propMeta.nameI18n;
    return i18nKey
      ? t(i18nKey, { defaultValue: fallbackLabel })
      : fallbackLabel;
  }, [propMeta.name, propMeta.nameI18n]);

  const CustomFieldComponent = useMemo(() => {
    if (
      customEditorFields &&
      Object.hasOwn(customEditorFields, propMeta.type)
    ) {
      return customEditorFields[propMeta.type];
    }
    return null;
  }, [customEditorFields, propMeta.type]);

  if (CustomFieldComponent) {
    return <CustomFieldComponent name={name} propMeta={propMeta} />;
  }

  // Custom input fields should be handled by custom editor components.
  // This check here improves the type narrowing of the switch statement below.
  if (propMeta.prop === "custom") {
    return null;
  }

  switch (propMeta.type) {
    case "string":
      return <StringField name={name} label={label} />;

    case "textarea":
      return <TextareaField name={name} label={label} />;

    case "number":
      return (
        <NumberField name={name} label={label} {...propMeta.numberProps} />
      );

    case "boolean":
      return <BooleanField name={name} label={label} />;

    case "circle-image-upload":
    case "background-image":
      return (
        <ImagePickerField
          name={name}
          label={label}
          shouldAllowRemovingImage={propMeta.shouldAllowRemovingImage}
        />
      );

    case "circle-video-upload":
      return <VideoUploader name={name} />;

    case "color":
    case "color-without-alpha":
      return (
        <ColorField
          name={name}
          label={label}
          shouldShowAlpha={propMeta.type === "color"}
        />
      );

    case "select":
      return (
        <SelectField name={name} label={label} options={propMeta.options} />
      );

    case "custom-html":
      return <CustomHtmlField name={name} label={label} />;

    case "list":
      return <ListField name={name} />;

    case "oembed":
      return <EmbedField name={name} label={label} />;

    case "slider": {
      if (isPropertiesPanelV2) {
        return (
          <DimensionInput
            name={name}
            label={label}
            units={propMeta.sliderProps.units as Unit[]}
          />
        );
      }
      return (
        <SliderField
          name={name}
          label={label}
          inputClassName="w-16"
          {...propMeta.sliderProps}
        />
      );
    }

    case "url":
      return <UrlField name={name} label={label} />;

    case "border-radius":
      return <BorderRadiusSelector name={name} />;

    case "alignment":
      return (
        <AlignmentField
          name={name}
          options={propMeta.alignment_options}
          label={label}
        />
      );

    case "dimension":
      return <DimensionInput name={name} label={label} />;

    case "sizing":
      return <Sizing />;

    case "box-model":
      return (
        <BoxModelInput
          name={name}
          label={label}
          type={propMeta.boxModelType ?? "padding"}
        />
      );

    case "heading-level-picker":
      return <HeadingLevelPicker label={label} />;

    case "rich-text":
      return <RichTextField name={name} propMeta={propMeta} />;
    case "paragraph-style-picker":
      return <ParagraphLevelPicker label={label} />;

    case "form-select":
      return <FormSelectField name={name} propMeta={propMeta} />;

    case "visibility":
      return <VisibilityField name={name} label={label} />;

    case "font-picker":
      return <FontPicker name={name} />;

    case "font-weight":
      return <FontWeight name={name} />;

    // eslint-disable-next-line sonarjs/no-duplicated-branches -- work in progress
    case "flex-direction":
      /* TODO: We need designs for the different values */
      /* if (isPropertiesPanelV2) {
        return (
          <DirectionField
            name={name}
            label={label}
            options={propMeta.options}
          />
        );
      } */

      return (
        <SelectField name={name} label={label} options={propMeta.options} />
      );

    case "flex-wrap":
      if (isPropertiesPanelV2) {
        return (
          <WrapField name={name} label={label} options={propMeta.options} />
        );
      }
      return (
        <SelectField name={name} label={label} options={propMeta.options} />
      );

    case "display":
      /* TODO: We need designs for the different values */
      // if (isPropertiesPanelV2) {
      //   return (
      //     <DisplayField name={name} label={label} options={propMeta.options} />
      //   );
      // }
      return null;

    default:
      return null;
  }
};
